import { useState, useEffect, RefObject } from 'react';

interface Dimensions {
  width: number;
  height: number;
}

/**
 * Track displayed dimensions of an image element via ResizeObserver.
 * Resets on URL change and handles cached images correctly.
 */
export function useImageDimensions(
  ref: RefObject<HTMLImageElement | null>,
  deps: unknown[] = [],
  enabled: boolean = true
): Dimensions | null {
  const [dimensions, setDimensions] = useState<Dimensions | null>(null);

  useEffect(() => {
    // Reset when deps change
    setDimensions(null);

    if (!enabled || !ref.current) return;

    const updateDimensions = () => {
      if (ref.current) {
        const { clientWidth, clientHeight } = ref.current;
        if (clientWidth > 0 && clientHeight > 0) {
          setDimensions({ width: clientWidth, height: clientHeight });
        }
      }
    };

    const img = ref.current;
    img.addEventListener('load', updateDimensions);

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setDimensions({ width, height });
        }
      }
    });

    observer.observe(img);

    // Handle cached images that are already loaded
    if (img.complete && img.naturalWidth > 0) {
      updateDimensions();
    }

    return () => {
      img.removeEventListener('load', updateDimensions);
      observer.disconnect();
    };
  }, [...deps, enabled]);

  return dimensions;
}
